import React, { useContext, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Box, Container, Stack, Typography } from '@mui/material';
import { Clock } from 'lucide-react';
import { Button as UiButton } from '@repo/ui';

import { AuthErrorMessage } from 'components/auth';
import { IconBadge } from 'components/UI/IconBadge';
import { Input } from 'components/UI/Input';
import { CompanyContext } from 'contexts/companyContext';
import { useAuth } from 'hooks';
import { useConfirmReplacementReceipt } from 'hooks/api/useConfirmReplacementReceipt';
import { useSendMagicAuthCode } from 'hooks/api/useWorkOSAuth';
import { emailRex } from 'settings';

import { OnboardingAuthPanel } from '../onboarding/components/OnboardingAuthPanel';

/**
 * VRG-275 — what a HELD replacement tag resolves to.
 *
 * Reached by redirect from `GET /verify/:uid` when the scanned tag is `held`: bound
 * to a certificate but deliberately not yet live, because the brand mailed it to the
 * owner instead of fitting it on the bench (`nfc-verification.service.ts`,
 * `invalidReason: 'tag_held'`). It is the sibling of `pages/TagReplaced` — read that
 * page's header comment first — and exists for the opposite reason: TagReplaced is
 * shown to someone holding a tag that is FINISHED and must not reassure them, because
 * a loose genuine tag is indistinguishable from a reproduction. This page is shown to
 * someone holding a tag that has NEVER been active — most likely the legitimate owner
 * who just opened an envelope from the brand — so it addresses them directly and
 * offers the one thing that makes the tag real: confirming they have it.
 *
 * WHO MAY ACTIVATE A HELD TAG (product decision, 2026-08-12, task DECIDED section):
 * the owner, signed in. Not "the first scan" — that is a security no-op, since an
 * interceptor scans it and it goes live just as easily as the real owner would. So
 * this page is a CLAIM flow, not a passive notice: "sign in to confirm you received
 * it" is the primary action, reusing the VERA-839 Claim-Ownership Sign-Up shape (an
 * owner may not have a Vera account yet — `owners.user_id` is nullable — so the
 * account is created as part of confirming, not required beforehand). If the visitor
 * is already authenticated, signing in again would be pure friction, so that step is
 * skipped and "Confirm you received it" is offered directly.
 *
 * Confirming calls `POST /certificates/:certificateId/replacement/confirm`. The API
 * refuses (404 — deliberately the SAME shape as "no such certificate", per
 * `ConfirmTagReplacementByOwnerUseCase`'s own comment) anyone whose authenticated
 * identity is not the certificate's owner — that refusal IS the security property
 * (see the task's DECIDED section) — so a refusal here must NOT read as an
 * accusation: the visitor may be a family member, or bought the item secondhand.
 * It points at the brand, same as TagReplaced.
 *
 * Deliberately absent, same rule as TagReplaced:
 *   - any mention of the old tag, or that a replacement/rebind ever happened
 *   - any accusation, or wording implying the item is counterfeit or stolen
 *
 * No Vera branding of its own — served from the company's Vera-hosted installation
 * on the brand's subdomain, themed by `App.tsx` from that subdomain, same as every
 * other page in this app.
 *
 * `certificateId` is read from the query string. Unlike TAG_REPLACED (which
 * deliberately omits the scanned uid — that page has no action to take, so
 * echoing an identifier into the URL would only add exposure), this page's whole
 * purpose is a scoped action against ONE certificate, and the confirm endpoint
 * requires that id as a route param. If it is ever missing (a stale link, or the
 * redirect not yet carrying it), there is nothing safe to act on — the page falls
 * back to pointing the visitor at the brand rather than guessing.
 *
 * ⚠️ KNOWN INTEGRATION GAP (2026-08-12): as built, `GET /verify/:uid`'s redirect to
 * this page (`verify.public.controller.ts`, the `tag_held` branch) carries NO query
 * string at all — it copies the `tag_replaced` branch's URL verbatim ("no scanned
 * uid echoed"), but that reasoning doesn't transfer here: TAG_REPLACED has no
 * action, this page's entire point IS one, scoped to a certificateId nothing
 * currently supplies. Until the redirect is amended to append `?certificateId=`
 * (or a "my pending replacement" lookup is added), a real physical tap lands here
 * with `certificateId` null and the confirm action is unreachable — the "we
 * couldn't tell which item this is for" fallback below is what a live tap sees
 * today. This file is written against the intended contract so the fix is a
 * one-line redirect change, not a rebuild.
 */
export default function TagNotActive() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();
  const { companyId } = useContext(CompanyContext);

  const certificateId = searchParams.get('certificateId');

  // Unauthenticated "type your email, get a code" step. Mirrors
  // `CreateAccountEmail`'s pre-panel step — `OnboardingAuthPanel` itself has no
  // email field, it only sends/verifies a code for an email it's already given.
  const [email, setEmail] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [sendError, setSendError] = useState<React.ReactNode>(null);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const sendCodeInFlightRef = useRef(false);

  const [confirmError, setConfirmError] = useState<React.ReactNode>(null);
  const [notOwner, setNotOwner] = useState(false);

  const sendMagicAuthCode = useSendMagicAuthCode();
  const confirmReceipt = useConfirmReplacementReceipt();

  const handleConfirm = async () => {
    if (!certificateId) return;

    setConfirmError(null);
    setNotOwner(false);
    try {
      await confirmReceipt.mutateAsync(certificateId);
      // The certificate is live now — that's what they came here for.
      navigate(`/certificates/${certificateId}`);
    } catch (err: any) {
      const status = err?.response?.status;
      // `ConfirmTagReplacementByOwnerUseCase` deliberately returns the SAME 404
      // shape for "no such certificate" and "signed in as the wrong owner" — it
      // must never confirm to a stranger that a certificateId exists and has a
      // tag pending activation (see the use case's own comment). So 404 here
      // means "not you", not "broken link" — the non-accusatory message covers
      // both without distinguishing them, which is the point.
      if (status === 404) {
        setNotOwner(true);
      } else if (status === 400) {
        // CertificateGeneralError — the certificate has no replacement tag
        // awaiting activation (already confirmed, e.g. from another tab, or
        // released by the brand's operational escape hatch).
        setConfirmError(
          'This tag has already been activated, or there is nothing waiting on it. Try opening your certificate directly.'
        );
      } else {
        setConfirmError('Something went wrong confirming your tag. Please try again.');
      }
    }
  };

  const handleSendCode = async () => {
    if (sendCodeInFlightRef.current) return;

    if (!emailRex.test(email.trim())) {
      setSendError('Enter a valid email address.');
      return;
    }

    sendCodeInFlightRef.current = true;
    setIsSendingCode(true);
    setSendError(null);
    try {
      await sendMagicAuthCode.mutateAsync({ email: email.trim(), companyId });
      setCodeSent(true);
    } catch (err: any) {
      const backendMsg: string | undefined = err?.response?.data?.message;
      const isRawStatus =
        !backendMsg ||
        /^(bad request|unauthorized|forbidden|not found|internal server error)$/i.test(backendMsg.trim());
      setSendError(isRawStatus ? 'Failed to send the code. Please try again.' : backendMsg);
    } finally {
      sendCodeInFlightRef.current = false;
      setIsSendingCode(false);
    }
  };

  return (
    <Container maxWidth='sm'>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '80vh',
          textAlign: 'center',
        }}
      >
        <Stack spacing={3} alignItems='center' sx={{ width: '100%', maxWidth: 360 }}>
          {/*
            The shared `IconBadge` — the grey circle every state screen in this app opens
            with. A clock, in the BRAND tone rather than the alert red.

            Unlike its siblings this page CAN say "yet" (its title already does): a held
            replacement tag is genuinely awaiting one specific event, and the person reading
            is most likely the owner who just opened the envelope. So the pending glyph is
            accurate here in a way it would not be on `CertificateNotActive`, whose header
            explains at length why a direction-of-travel signal is forbidden there.

            Nothing is broken, and the page's whole job is to invite an action, so a red
            alarm above a claim flow would be actively wrong.
          */}
          <IconBadge icon={Clock} />

          <Stack spacing={2} alignItems='center'>
            <Typography variant='h4' component='h1'>
              This tag isn't active yet
            </Typography>

            {/* Two sentences, not one joined by an em-dash (§15). lineHeight 1.5 so
                the copy doesn't read dense on a phone, the only device this page is
                ever seen on. */}
            <Typography variant='body1' color='text.secondary' sx={{ lineHeight: 1.5 }}>
              If this was just mailed to you, confirm you received it to activate your
              certificate.
            </Typography>
          </Stack>

          {notOwner ? (
            <Typography variant='body1' color='text.secondary' sx={{ lineHeight: 1.5 }}>
              This certificate is linked to a different account. If you received this
              item as a gift or bought it secondhand, contact the brand you bought it
              from for help.
            </Typography>
          ) : !certificateId ? (
            <Typography variant='body1' color='text.secondary' sx={{ lineHeight: 1.5 }}>
              We couldn't tell which item this is for. Please contact the brand you
              bought it from.
            </Typography>
          ) : isAuthenticated ? (
            <Stack spacing={2} sx={{ width: '100%' }}>
              <AuthErrorMessage message={confirmError} />
              <UiButton
                variant='primary'
                fullWidth
                onClick={handleConfirm}
                disabled={confirmReceipt.isPending}
              >
                {confirmReceipt.isPending ? 'Confirming…' : 'Confirm you received it'}
              </UiButton>
            </Stack>
          ) : codeSent ? (
            <Box sx={{ width: '100%' }}>
              <OnboardingAuthPanel
                email={email}
                companyId={companyId}
                initialCodeSent
                // The verify mutation dispatches SET_AUTH before this fires (see
                // `useVerifyMagicAuthCode`'s onSuccess), so the account exists and
                // the session is live the moment we get here — go straight to
                // confirming rather than making the owner click a second button.
                onVerified={() => {
                  void handleConfirm();
                }}
              />
            </Box>
          ) : (
            <Stack spacing={2} sx={{ width: '100%' }}>
              <Input
                type='email'
                value={email}
                onChange={setEmail}
                placeholder='Email'
                sx={{ '& input': { paddingLeft: '8px' } }}
                onKeyDown={(e: React.KeyboardEvent) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSendCode();
                  }
                }}
              />
              <AuthErrorMessage message={sendError} />
              <UiButton
                variant='primary'
                fullWidth
                onClick={handleSendCode}
                disabled={isSendingCode}
              >
                {isSendingCode ? 'Sending…' : 'Sign in to confirm you received it'}
              </UiButton>
            </Stack>
          )}

          <Typography variant='body2' color='text.secondary' sx={{ lineHeight: 1.5 }}>
            Questions? Contact the brand you bought it from. They can look it up for
            you.
          </Typography>
        </Stack>
      </Box>
    </Container>
  );
}
